import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { User, Envelope, Lock, ArrowLeft, CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";
import TotalTerminalVisionLogo from "../components/TotalTerminalVisionLogo";

export default function ForgotPassword() {
    const nav = useNavigate();
    const [identifier, setIdentifier] = useState("");
    const [name, setName] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const looksLikeEmail = identifier.includes("@");

    const submit = async (e) => {
        e.preventDefault();
        if (newPassword.length < 6) { toast.error("New password must be at least 6 characters"); return; }
        if (newPassword !== confirm) { toast.error("New passwords don't match"); return; }
        try {
            setLoading(true);
            await api.post("/auth/reset-password", {
                identifier: identifier.trim(),
                name: name.trim() || undefined,
                new_password: newPassword,
            });
            setDone(true);
            toast.success("Password updated — you can now sign in");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="ttv-login-shell" data-testid="forgot-password-page">
            <div className="ttv-bg-image" aria-hidden />
            <div className="ttv-bg-overlay" aria-hidden />
            <div className="ttv-bg-grid" aria-hidden />

            <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-10">
                <div className="mb-8 flex justify-center">
                    <TotalTerminalVisionLogo size="lg" />
                </div>

                <div className="ttv-card" role="dialog" aria-labelledby="ttv-forgot-title">
                    <div className="flex flex-col items-center text-center mb-6">
                        <div className="ttv-user-badge">
                            <Lock size={22} weight="duotone" />
                        </div>
                        <h1 id="ttv-forgot-title" className="font-display font-black text-slate-900 text-3xl mt-3">
                            Reset Password
                        </h1>
                        <p className="text-slate-500 text-sm mt-1.5">
                            {done
                                ? "Your password has been updated"
                                : <>Enter your <span className="text-[#2563EB] font-semibold">username</span> or <span className="text-[#2563EB] font-semibold">email</span> to recover access.</>}
                        </p>
                    </div>
                    <div className="h-px bg-slate-200 mb-6" />

                    {done ? (
                        <div className="text-center space-y-4" data-testid="forgot-password-success">
                            <CheckCircle size={56} weight="duotone" className="text-emerald-500 mx-auto" />
                            <div className="text-slate-800 font-semibold">You're all set</div>
                            <div className="text-sm text-slate-500">Sign in with your new password to continue.</div>
                            <button onClick={() => nav("/login")} className="ttv-btn-primary" data-testid="back-to-login-btn">
                                Go to sign in
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
                            {/* Identifier — username OR email */}
                            <div>
                                <label className="ttv-label">Username or Email</label>
                                <div className="relative">
                                    {looksLikeEmail
                                        ? <Envelope size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        : <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />}
                                    <input
                                        className="ttv-input pl-11"
                                        placeholder="Enter your username or email"
                                        value={identifier}
                                        onChange={(e) => setIdentifier(e.target.value)}
                                        required
                                        data-testid="forgot-identifier"
                                        autoComplete="username"
                                    />
                                </div>
                                <div className="text-[11px] text-slate-500 mt-1.5">
                                    {looksLikeEmail ? "Looks like an email — we'll match against the address on your account." : "Enter the username you use to sign in."}
                                </div>
                            </div>

                            {/* Optional name */}
                            <div>
                                <label className="ttv-label">
                                    Full Name <span className="text-slate-400 font-normal">(optional 2nd-factor)</span>
                                </label>
                                <div className="relative">
                                    <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        className="ttv-input pl-11"
                                        placeholder="As it appears on your account"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        data-testid="forgot-name"
                                    />
                                </div>
                            </div>

                            {/* New password */}
                            <div>
                                <label className="ttv-label">New Password</label>
                                <div className="relative">
                                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type={showPw ? "text" : "password"}
                                        className="ttv-input pl-11 pr-11"
                                        placeholder="Minimum 6 characters"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        minLength={6}
                                        required
                                        data-testid="forgot-new-password"
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPw((s) => !s)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition"
                                        aria-label={showPw ? "Hide password" : "Show password"}
                                        data-testid="forgot-password-toggle"
                                    >
                                        {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="ttv-label">Confirm New Password</label>
                                <div className="relative">
                                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type={showPw ? "text" : "password"}
                                        className="ttv-input pl-11"
                                        placeholder="Retype the new password"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        minLength={6}
                                        required
                                        data-testid="forgot-confirm-password"
                                        autoComplete="new-password"
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="ttv-btn-primary"
                                disabled={loading}
                                data-testid="forgot-submit-btn"
                            >
                                {loading ? "Updating…" : "Update Password"}
                            </button>

                            <Link
                                to="/login"
                                className="text-xs text-slate-600 hover:text-[#2563EB] font-semibold inline-flex items-center gap-1.5 justify-center w-full pt-1"
                                data-testid="back-to-login-link"
                            >
                                <ArrowLeft size={12} weight="bold" /> Back to sign in
                            </Link>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
